import { castName } from "../art/cast";
import { $, h } from "../lib/dom";
import { t, tr } from "../i18n";
import { closeDialog, updateControls } from "./dialog";
import { backToTitle } from "./flow";
import { askJSON, localText, strList } from "./llm";
import { chName } from "./log";
import { recordGrade } from "./progress";
import { dept, L, S, sessionGuard } from "./state";
import { clock, renderHUD } from "./time";
import { closeZoom } from "./zoom";

type ItemResult = "good" | "partial" | "miss";

interface DebriefItem {
  title: string;
  result: ItemResult;
  comment: string;
}

interface Debrief {
  grade: string;
  headline: string;
  items: DebriefItem[];
  bestMoment: string;
  nextTime: string[];
}

const RESULT_LABEL: Record<ItemResult, string> = { good: t("fb.good"), partial: t("fb.partial"), miss: t("fb.miss") };

const isResult = (x: unknown): x is ItemResult => x === "good" || x === "partial" || x === "miss";
const str = (x: unknown): string => (typeof x === "string" ? localText(x) : "");

/** Validate the grader's JSON into a typed debrief (zh: Traditional-Chinese-normalised). */
export function normalizeDebrief(raw: Record<string, unknown>): Debrief | null {
  if (typeof raw.grade !== "string" || !raw.grade) return null;
  const items = Array.isArray(raw.items) ? (raw.items as unknown[]) : [];
  return {
    grade: raw.grade.slice(0, 1),
    headline: str(raw.headline),
    items: items.map((it) => {
      const o = (typeof it === "object" && it !== null ? it : {}) as Record<string, unknown>;
      return { title: str(o.title), result: isResult(o.result) ? o.result : "partial", comment: str(o.comment) };
    }),
    bestMoment: str(raw.best_moment),
    nextTime: strList(raw.next_time).map(localText),
  };
}

function playRecord(): string {
  return S.log
    .map((e) => {
      const who = e.kind === "me" ? "Clerk" : e.kind === "narr" ? tr("旁白", "Narrator") : castName(e.who);
      return `[${clock(e.t)}][${chName(e.ch)}] ${who}${tr("：", ": ")}${e.text}`;
    })
    .join("\n");
}

function graderPrompt(reason: string): string {
  const record = playRecord().slice(-60000);
  const rubric = L.rubric.map((x, i) => `${i + 1}. ${x}`).join("\n");
  const facts = L.facts().join("\n");
  return tr(
    `你是${dept.grader}，要為見習醫學生（clerk）在一場模擬遊戲中的表現寫評語。
${L.graderCase}
【評分重點，各一項】
${rubric}
【結束方式】${reason}
【關鍵事實】
${facts}
【clerk 實際做過與知道的事】
${L.known()}
【完整紀錄】
${record}
一律使用台灣繁體中文與台灣用語（絕對不要出現簡體字），語氣像嚴格但願意教的老師，具體、提到時間點。只輸出 JSON：
{"grade":"A","headline":"一句話總評","items":[{"title":"短標題","result":"good","comment":"1 到 2 句"}],"best_moment":"他做得最好的一個具體時刻","next_time":["...","...","..."]}
items 依評分重點各一項。result 只能是 good、partial、miss；grade 只能是 A、B、C、D。`,
    `You are ${dept.grader}, writing feedback on how a clerk (a medical student on their clerkship) did in a simulation game.
${L.graderCase}
[GRADING POINTS, one item each]
${rubric}
[HOW IT ENDED] ${reason}
[KEY FACTS]
${facts}
[WHAT THE CLERK ACTUALLY DID AND FOUND OUT]
${L.known()}
[FULL LOG]
${record}
Write in American English, never Chinese, in the voice of a demanding attending who genuinely wants to teach: specific, and citing the clock times. Output JSON only:
{"grade":"A","headline":"one-sentence overall verdict","items":[{"title":"short title","result":"good","comment":"1 to 2 sentences"}],"best_moment":"the one specific moment they handled best","next_time":["...","...","..."]}
One item per grading point. result must be good, partial or miss; grade must be A, B, C or D.`,
  );
}

function renderDebrief(box: HTMLElement, r: Debrief | null): void {
  box.replaceChildren();
  if (r) {
    const ending = S.ending ? t("fb.ending", { code: S.ending.code, name: S.ending.name }) : "";
    box.append(
      h("div", { className: "grade", text: r.grade }),
      h("h3", { text: t("fb.heading", { name: castName(dept.attending), level: L.short, ending }) }),
      h("p", { className: "head", text: r.headline }),
    );
    for (const it of r.items)
      box.appendChild(
        h(
          "div",
          { className: `item ${it.result}` },
          h("b", { text: it.title }, h("span", { text: RESULT_LABEL[it.result] })),
          h("p", { text: it.comment }),
        ),
      );
    if (r.bestMoment) {
      const p = h("p", { text: r.bestMoment });
      p.style.margin = "0";
      box.append(h("h4", { text: t("fb.best") }), p);
    }
    if (r.nextTime.length)
      box.append(h("h4", { text: t("fb.next") }), h("ol", {}, ...r.nextTime.map((x) => h("li", { text: x }))));
  } else box.append(h("h3", { text: t("fb.over") }), h("p", { text: t("fb.failed") }));

  box.appendChild(h("div", { className: "facts", text: L.facts().join(t("fb.factSep")) }));
  const again = h("button", { className: "btn primary", text: t("end.toLevels") });
  again.onclick = backToTitle;
  const back = h("button", { className: "btn", text: t("fb.backToLog") });
  back.onclick = () => {
    $("#debrief").classList.remove("show");
    closeDialog();
  };
  const row = h("div", {}, again, back);
  row.style.cssText = "display:flex;gap:10px;margin-top:16px;flex-wrap:wrap";
  box.appendChild(row);
}

/** End the level and ask the attending (the model) for a graded debrief. */
export async function finish(reason: string): Promise<void> {
  if (!S.live || S.finishing) return;
  S.finishing = true;
  S.ended = true;
  closeZoom();
  renderHUD();
  if (S.dialog) updateControls();
  $("#endCard").classList.remove("show");
  $("#cineBox").classList.remove("show");
  const box = $("#debriefBody");
  box.replaceChildren(
    h("h3", { text: t("fb.over") }),
    h("p", { text: t("fb.writing", { name: castName(dept.attending) }) }),
  );
  (box.lastChild as HTMLElement).style.color = "var(--ink2)";
  $("#debrief").classList.add("show");

  const alive = sessionGuard();
  const r = await askJSON(graderPrompt(reason), "default").then(normalizeDebrief, () => null);
  if (!alive()) return;
  if (r) recordGrade(L.id, r.grade);
  renderDebrief(box, r);
}

/** Destructive button that needs a second click within 4 s (or skips it when `needConfirm` says so). */
export function twoClick(
  btn: HTMLButtonElement,
  label: string,
  armedLabel: string,
  action: () => void,
  needConfirm?: () => boolean,
): void {
  let armed: ReturnType<typeof setTimeout> | null = null;
  const reset = (): void => {
    if (armed) clearTimeout(armed);
    armed = null;
    btn.textContent = label;
    btn.classList.remove("primary");
  };
  btn.onclick = () => {
    if (armed || (needConfirm && !needConfirm())) {
      reset();
      action();
      return;
    }
    btn.textContent = armedLabel;
    btn.classList.add("primary");
    armed = setTimeout(reset, 4000);
  };
}

export function initDebrief(): void {
  $("#finishBtn").onclick = () => void finish(L.outcomeText?.() ?? t("end.done"));
  twoClick($<HTMLButtonElement>("#endBtn"), t("stage.end"), t("stage.endArmed"), () => void finish(t("end.early")));
  twoClick(
    $<HTMLButtonElement>("#menuBtn"),
    t("stage.levels"),
    t("stage.levelsArmed"),
    backToTitle,
    () => S.live && S.started && !S.ended,
  );
  $("#endFb").onclick = () => {
    $("#endCard").classList.remove("show");
    if (S.live && S.ending) void finish(t("end.reason", { code: S.ending.code, name: S.ending.name }));
  };
  $("#endMenu").onclick = backToTitle;
}
